import { ref } from 'vue'
import { getStoredAdmin } from '@/services/api'
import { useFocusMode } from '@/composables/useFocusMode'

/** Resolved lazily inside the handler so this module does not depend on focus mode at import time. */
const toggleFocusMode = () => useFocusMode().toggle()

/**
 * Global keyboard shortcuts for the admin shell. Installed once from AppShell. Shortcuts are ignored while the
 * caret is in a text field (so typing "g" in a search box doesn't navigate). Two-key "g then x" navigation, "/"
 * to focus the page search, and "?" to toggle the help modal. Module-level singleton state so the help modal
 * reads the same `helpOpen` ref the listener flips.
 */
const helpOpen = ref(false)

/**
 * The shortcut map, and the permission each destination needs.
 *
 * ── why these carry permissions ─────────────────────────────────────────────────────────────────────
 * This is a SECOND navigation surface, and only the first one was gated. `AppSidebar` wraps every row in
 * `can(...)`, but the `?` overlay rendered this array unfiltered — so a contracted external reviewer got
 * a labelled directory of the internal panel («داشبورد», «دانش‌آموزان», «جستجوی سراسری», «عملیات
 * پلتفرم»), naming four surfaces they are not supposed to know exist. Worse than the naming: `g l`
 * actually navigated, because `/learners` carried no route permission either.
 *
 * Both halves are closed now — the routes are gated and this list is filtered — and they are independent
 * on purpose. Hiding a shortcut is courtesy; the router refusing the navigation is the boundary.
 *
 * `permission: null` means «everyone», and it is spelled out rather than omitted so that adding an entry
 * without thinking about who sees it is a visible act rather than a default.
 */
export const SHORTCUTS = [
  { keys: ['/'], label: 'تمرکز روی جست‌وجو', kind: 'focus-search', permission: null },
  { keys: ['g', 'd'], label: 'داشبورد', path: '/', permission: 'dashboard.view' },
  { keys: ['g', 'r'], label: 'صف بازبینی', path: '/question-bank/review-queue', permission: 'questions.review' },
  { keys: ['g', 'l'], label: 'دانش‌آموزان', path: '/learners', permission: 'learners.read' },
  { keys: ['g', 'q'], label: 'بانک سؤال', path: '/question-bank/questions', permission: 'questions.read_key' },
  { keys: ['g', 's'], label: 'جست‌وجوی سراسری', path: '/search', permission: 'learners.read' },
  { keys: ['g', 'o'], label: 'عملیات پلتفرم', path: '/ops/health', permission: 'ops.read' },
  { keys: ['f'], label: 'حالت تمرکز (جمع‌کردن منو و نوار بالا)', kind: 'focus-mode', permission: null },
  { keys: ['?'], label: 'نمایش این راهنما', kind: 'help', permission: null },
]

/**
 * ── the reason none of this worked ─────────────────────────────────────────────────────────────────
 *
 * Every comparison in this file used to be against `event.key`, which is the CHARACTER the layout
 * produces. These operators are Konkur teachers typing on a Persian keyboard, where the physical G key
 * emits «ه», F emits «ب» and Q emits «ض». `e.key === 'g'` is false for all of them, so the entire
 * shortcut map — advertised in the help overlay, documented, tested — was dead on the only layout the
 * people it was built for actually use. It worked in every test and for every engineer, because both
 * type on a Latin layout.
 *
 * `event.code` names the PHYSICAL key and does not move with the layout: the same key is `KeyG` in
 * Tehran and in California. So the map is matched on `code`, with `key` kept as a fallback for the two
 * cases where `code` is absent or wrong — a synthetic event (every test in this file's spec dispatches
 * one), and a layout whose `/` sits somewhere other than the ANSI Slash position.
 *
 * The letters here are ASCII names for physical positions, NOT for characters. `g` below means «the key
 * an ANSI keyboard prints G on», whatever it prints in this operator's language.
 */
const physicalKey = (e) => {
  // KeyA…KeyZ is the only family this map binds, and it is layout-independent.
  if (typeof e.code === 'string' && /^Key[A-Z]$/.test(e.code)) return e.code.slice(3).toLowerCase()

  // No usable code: a synthetic event, an IME, or a key outside the letter block. Fall back to the
  // character, which is right on a Latin layout and no worse than the old behaviour anywhere else.
  return typeof e.key === 'string' && e.key.length === 1 ? e.key.toLowerCase() : ''
}

/** `/` — the ANSI Slash position, or any layout that genuinely produces the character. */
const isSlash = (e) => e.code === 'Slash' || e.key === '/'

/** `?` — Shift over the same physical key, or a layout that produces «?» directly. */
const isQuestionMark = (e) => (e.code === 'Slash' && e.shiftKey) || e.key === '?'

let installed = false

/**
 * Whether this operator may use a shortcut.
 *
 * Reads the stored session directly rather than through `usePermission`, because the key handler is a
 * plain `window` listener with no component instance behind it — the same reason `router/index.js` has
 * its own `hasPermission`. Both must stay in step with `can()`: superadmins hold `system.all`.
 */
const allowed = (shortcut) => {
  if (!shortcut?.permission) return true
  const perms = getStoredAdmin()?.permissions ?? []

  return perms.includes('system.all') || perms.includes(shortcut.permission)
}

const isTyping = (el) =>
  !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable)

export function useKeyboardShortcuts() {
  const install = (router) => {
    if (installed) return
    installed = true

    let pendingG = false
    let gTimer = null

    const onKey = (e) => {
      // Escape always closes the help overlay, even from a field.
      if (e.key === 'Escape' && helpOpen.value) {
        helpOpen.value = false
        return
      }
      if (isTyping(e.target) || e.metaKey || e.ctrlKey || e.altKey) return

      const pressed = physicalKey(e)

      if (pendingG) {
        pendingG = false
        clearTimeout(gTimer)
        const sc = SHORTCUTS.find((s) => s.keys[0] === 'g' && s.keys[1] === pressed)
        // `allowed()` here as well as in the modal: the overlay is what an operator READS, this is what
        // actually moves them. A shortcut hidden from the list but still live would be the worst of both.
        if (sc?.path && router && allowed(sc)) {
          e.preventDefault()
          router.push(sc.path)
        }
        return
      }

      if (pressed === 'g') {
        pendingG = true
        gTimer = setTimeout(() => { pendingG = false }, 1200)
        return
      }

      // `?` before `/`: on an ANSI board they are the same physical key and only Shift separates them.
      if (isQuestionMark(e)) {
        e.preventDefault()

        // ── why this asks the page first ────────────────────────────────────────────────────────
        // `?` used to fire TWO independent handlers. The review queue installs its own `keydown`
        // listener and its own help panel, so one press opened two overlays, stacked, and NEITHER
        // listed the other's keys: the global panel showed the `g …` navigation and no verdict keys,
        // the page panel showed the verdict keys and no navigation. An operator pressing the key that
        // means «what can I do here» got two partial answers and a stacking-context accident.
        //
        // Same contract the `/` shortcut below already uses, and for the same reason: a page that owns
        // a richer version of this affordance handles the event and calls `preventDefault()`, and the
        // global overlay stays shut. A page that does nothing still gets the global overlay, so this is
        // safe to land before any page opts in.
        const claimed = !window.dispatchEvent(new CustomEvent('admin:toggle-help', { cancelable: true }))
        if (!claimed) helpOpen.value = !helpOpen.value

        return
      }

      if (isSlash(e)) {
        e.preventDefault()

        // Ask the page first, then fall back to the old blind query.
        //
        // The bare `querySelector(...).focus()` is a silent no-op whenever the search box is inside a
        // collapsed region: `v-show` leaves the input in the DOM with `display:none`, and focusing a
        // display:none element does nothing at all. On the question bank that is precisely when an
        // operator reaches for `/` — the filter panel is folded, and focus mode folds it unconditionally
        // — so the shortcut advertised in the help overlay as «تمرکز روی جستجوی صفحه» did nothing on the
        // one screen with the most to search.
        //
        // A page that owns a foldable search box listens for this, opens the panel and focuses the input
        // itself. Pages that do not still get the old behaviour, so nothing had to change anywhere else.
        const handled = !window.dispatchEvent(new CustomEvent('admin:focus-search', { cancelable: true }))
        if (!handled) document.querySelector('input[type="search"]')?.focus()

        return
      }
      // Single-key, and safe because `isTyping` already excluded every field: an operator working a long
      // table wants the chrome gone without reaching for the mouse, and reaching for the mouse is the
      // thing focus mode exists to reduce.
      if (pressed === 'f') {
        e.preventDefault()
        toggleFocusMode()
      }
    }

    window.addEventListener('keydown', onKey)
  }

  // The overlay lists only what this operator can actually use.
  //
  // A FUNCTION, not a computed. `allowed()` reads the session out of localStorage, which is not
  // reactive, so a computed would cache its first evaluation and never invalidate — the list would
  // freeze at whichever session happened to be loaded when the composable was first called, and after a
  // re-login without a reload it would show the previous operator's surfaces. Called from the template,
  // this re-evaluates on every render of the overlay, which is exactly when the answer is needed.
  const visibleShortcuts = () => SHORTCUTS.filter(allowed)

  return { install, helpOpen, visibleShortcuts }
}
